/* eslint-disable no-plusplus */
import React, { FunctionComponent, useEffect, useState, useMemo, useCallback } from 'react';
import { useMediaQuery } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import routerUtils from '@modules/utilities/utils/router';
import { useRouter } from 'next/router';
import { v4 as uuidv4 } from 'uuid';
import { getTreeItemElementId } from '@modules/ui/shared/components/Tree';
import {
  buildTreeNodeAndUpdatePathMap,
  getParentNodeId,
  getNodeIdsToExpand,
  getTreePathById,
} from '@modules/ui/shared/utils/tree';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import Navigation from '@modules/utilities/interfaces/Navigation';
import { getUrlData } from '@modules/localization/utils/localeUtils';
import { addLeadingSlash } from '@modules/utilities/utils/path';
import { ReferenceType } from '@modules/client/types/globalTypes';
import NavigationContent from '@modules/utilities/interfaces/NavigationContent';
import { CLOUD_PAGE_IAV2_OPT_OUT_COOKIE } from '@modules/utilities/constants/cloudPageOptOutCookieConstants';
import { getCookieValueByKey } from '@rbx/core';
import SideBarContentContext, { TTreeListData } from '../SideBarContentContext';
import { showElementInViewById } from '../utils/dom';
import TreeNode from '../interfaces/TreeNode';
import { getTranslatedNodeIdForReferenceType } from '../../../apiReference/page/utils/sidebar';
import {
  isIAV2DomainNav,
  isIAV2FeaturesNav,
  isIAV1FeaturesNav,
  isIAV1CloudNav,
  isIAV1LegacyNav,
} from '../utils/cloudPages';

type TSideBarContentProviderProps = {
  navigation?: Navigation;
  urlBasePath?: string;
};

type TreePathItem = TreeNode & { isHeading?: boolean };

const filterContent = (content: NavigationContent[]): NavigationContent[] => {
  let filteredContent = content;

  // NOTE (gxue, 2025-11-06):
  // If IAV2 pages are enabled and the user has not opted out, filter out the IAV1 pages
  // Otherwise, filter out the IAV2 pages
  // See https://github.com/Roblox/creator-docs-staging/blob/3a0d54ab085d38b8810a639b3a5bae5f9add06a6/content/common/navigation/cloud/reference.yaml#L41
  if (
    process.env.enableOpenCloudIAV2Pages &&
    getCookieValueByKey(CLOUD_PAGE_IAV2_OPT_OUT_COOKIE.Name) === null
  ) {
    filteredContent = filteredContent.filter((item) => {
      return !isIAV1FeaturesNav(item) && !isIAV1CloudNav(item) && !isIAV1LegacyNav(item);
    });
  } else {
    filteredContent = filteredContent.filter(
      (item) => !isIAV2FeaturesNav(item) && !isIAV2DomainNav(item),
    );
  }

  return filteredContent;
};

const getTreePath = (treeListData: TTreeListData, id: string): TreePathItem[] | null => {
  for (let i = 0; i < treeListData.length; i++) {
    const { heading, nodes } = treeListData[i];

    for (let j = 0; j < nodes.length; j++) {
      const path = getTreePathById(nodes[j], id);
      if (path) {
        return heading
          ? [{ id: heading, value: heading, isHeading: true, path: null, children: null }, ...path]
          : path;
      }
    }
  }
  return null;
};

const SideBarContentProvider: FunctionComponent<
  React.PropsWithChildren<TSideBarContentProviderProps>
> = ({ children, navigation, urlBasePath }) => {
  const { translate } = useTranslation();
  const { breadcrumbs, isLandingPage } = useAppConfig();
  const [hasLeftNav, setHasLeftNav] = useState(!isLandingPage);
  const [open, setOpen] = useState(true);
  const [sidebarSessionId, setSidebarSessionId] = useState<string>('');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  const router = useRouter();
  const locationHash = typeof window !== 'undefined' ? window.location.hash : null;
  const nodeData = useMemo<{
    treeListData: TTreeListData;
    nodePathToIdMap: Map<string, string>;
  }>(() => {
    const nodePathToIdMap = new Map<string, string>();
    if (!navigation || !urlBasePath) {
      return {
        nodePathToIdMap,
        treeListData: [],
      };
    }
    const { navigationContent: content } = navigation;
    const treeListData = filterContent(content).map(({ heading, navigation: navigationNode }) => ({
      heading,
      nodes: navigationNode.map((nav) =>
        buildTreeNodeAndUpdatePathMap(nav, heading ?? '', urlBasePath, nodePathToIdMap),
      ),
    }));
    return {
      treeListData,
      nodePathToIdMap,
    };
  }, [navigation, urlBasePath]);

  const getCurrentPathNodeId = useCallback(() => {
    // remove the siteBasePath here
    // if siteBasePath = /docs, location.pathname = /docs/edu/a/b/c
    // final siteBasePath = /edu/a/b/c
    let pathname = routerUtils.getPathName(router);
    // eslint-disable-next-line prefer-destructuring -- NOTE (@mbae, 06/10/24): process.env should NOT be destructured in Next.js
    const siteBasePath = process.env.siteBasePath;
    pathname =
      siteBasePath && pathname.startsWith(siteBasePath)
        ? pathname.substring(siteBasePath.length)
        : pathname;
    // TODO (@ahua, CRK-3699): replace in the future with a more robust solution
    pathname = addLeadingSlash(getUrlData(pathname).subPath);
    const pathnameWithSlash = pathname.endsWith('/') ? pathname : `${pathname}/`;
    const pathnameWithoutSlash = pathname.endsWith('/')
      ? pathname.substring(0, pathname.length - 1)
      : pathname;
    // handle path with hash and take higher priority
    if (locationHash) {
      const id = nodeData.nodePathToIdMap.get(pathnameWithoutSlash + locationHash);
      if (id) return id;
    }
    // check both the paths with/without the last '/'
    // some paths defined in navigation has the last '/', while some are not
    const currentPathNodeId =
      nodeData.nodePathToIdMap.get(pathnameWithSlash) ??
      nodeData.nodePathToIdMap.get(pathnameWithoutSlash);
    return currentPathNodeId;
  }, [nodeData.nodePathToIdMap, locationHash, router]);

  const getCurrentTreePath = useCallback(() => {
    const nodeId = getCurrentPathNodeId();
    return nodeId ? getTreePath(nodeData.treeListData, nodeId) : null;
  }, [getCurrentPathNodeId, nodeData.treeListData]);

  const getBreadcrumbsItems = useCallback(() => {
    const treePath = getCurrentTreePath();
    if (treePath === null || treePath.length <= 1) {
      return breadcrumbs;
    }
    return [
      ...breadcrumbs,
      ...treePath
        // remove the current node
        .slice(0, treePath.length - 1)
        .filter((item) => !item.isHeading)
        .map((item) => ({ label: item.value, path: item.path })),
    ];
  }, [breadcrumbs, getCurrentTreePath]);

  const { selected: selectedByPath, expanded: expandedByPath } = useMemo(() => {
    const defaultValue: { selected: string; expanded: string[] } = {
      selected: '',
      expanded: [],
    };
    const nodeId = getCurrentPathNodeId();
    if (!nodeId) {
      return defaultValue;
    }
    defaultValue.selected = nodeId;
    const parentNodeId = getParentNodeId(nodeId);
    if (parentNodeId) {
      const newNodes = getNodeIdsToExpand(parentNodeId, defaultValue.expanded);
      defaultValue.expanded = [...newNodes];
    }
    return defaultValue;
  }, [getCurrentPathNodeId]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const [selected, setSelected] = useState<string>('');
  const classesNodeId = getTranslatedNodeIdForReferenceType(ReferenceType.CLASS, translate);

  let pathname = '';
  if (typeof window !== 'undefined') {
    pathname = window.location.pathname;
  }

  const hasEngineClasses = useMemo(
    () =>
      nodeData.treeListData.some((section) =>
        section.nodes.some((node) => node.id === classesNodeId),
      ),
    [classesNodeId, nodeData.treeListData],
  );

  const resetSidebarSessionId = useCallback(() => {
    setSidebarSessionId(uuidv4());
  }, []);

  useEffect(() => {
    setExpanded((e) => [...e, ...expandedByPath]);
    setSelected(selectedByPath);
    showElementInViewById(getTreeItemElementId(selectedByPath));
    // NOTE (tchu, 2025-06-24): Generate new sidebar session ID when path changes
    resetSidebarSessionId();
    // TODO (ahua, 2024/01/22): clean up dependencies required
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [isMobile]);

  return (
    <SideBarContentContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        hasEngineClasses,
        hasLeftNav,
        setHasLeftNav,
        content: navigation?.navigationContent,
        backTo: navigation?.backTo,
        expanded,
        selected,
        open,
        setExpanded,
        setOpen,
        setSelected,
        treeListData: nodeData.treeListData,
        getBreadcrumbsItems,
        sidebarSessionId,
        resetSidebarSessionId,
      }}>
      {children}
    </SideBarContentContext.Provider>
  );
};

export default SideBarContentProvider;
